/**
 * Задаване на парола по линк от писмо — и при покана, и при забравена парола.
 *
 * Екранът е публичен: човекът още няма с какво да влезе.
 */

import { useState } from 'react';
import {
  Alert,
  Button,
  Center,
  Loader,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { apiRequest } from '../api/client';

const MIN_LENGTH = 12;

interface TokenHolder {
  email: string;
  fullName: string;
  purpose: 'INVITE' | 'RESET';
}

export function SetPasswordPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isDone, setDone] = useState(false);

  const holder = useQuery({
    queryKey: ['password-token', token],
    queryFn: () => apiRequest<TokenHolder>(`/api/auth/password-token/${encodeURIComponent(token)}`),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: () => apiRequest('/api/auth/password', { method: 'POST', body: { token, password } }),
    onSuccess: () => setDone(true),
  });

  const problem =
    password.length > 0 && password.length < MIN_LENGTH
      ? `Паролата трябва да е поне ${MIN_LENGTH} символа`
      : confirmation.length > 0 && password !== confirmation
        ? 'Двете полета не съвпадат'
        : null;

  const canSubmit = password.length >= MIN_LENGTH && password === confirmation;

  if (holder.isLoading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  return (
    <Center h="100vh">
      <Paper withBorder p="xl" w={420}>
        {holder.isError ? (
          <Stack>
            <Title order={3}>Линкът е невалиден</Title>
            <Text size="sm" c="dimmed">
              Линкът е изтекъл или вече е използван. Поискайте нов от администратора или през
              „Забравена парола“.
            </Text>
            <Button variant="default" onClick={() => navigate('/')}>
              Към входа
            </Button>
          </Stack>
        ) : isDone ? (
          <Stack>
            <Title order={3}>Паролата е зададена</Title>
            <Text size="sm" c="dimmed">
              Вече можете да влезете с новата си парола.
            </Text>
            <Button onClick={() => navigate('/')}>Към входа</Button>
          </Stack>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (canSubmit) submit.mutate();
            }}
          >
            <Stack>
              <Title order={3}>
                {holder.data?.purpose === 'RESET' ? 'Нова парола' : 'Задаване на парола'}
              </Title>
              <Text size="sm" c="dimmed">
                {holder.data?.fullName} · {holder.data?.email}
              </Text>

              {submit.isError && (
                <Alert color="red">{(submit.error as Error).message}</Alert>
              )}

              <PasswordInput
                label="Парола"
                description={`Поне ${MIN_LENGTH} символа`}
                value={password}
                onChange={(event) => setPassword(event.currentTarget.value)}
                autoFocus
                required
              />
              <PasswordInput
                label="Повторете паролата"
                value={confirmation}
                onChange={(event) => setConfirmation(event.currentTarget.value)}
                error={problem}
                required
              />

              <Button type="submit" loading={submit.isPending} disabled={!canSubmit} fullWidth>
                Запазване
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
    </Center>
  );
}
